import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import * as Icons from 'lucide-react';
import { Plus, Trash2, Edit, Eye, EyeOff, X, LayoutDashboard, Upload, GripVertical, Search, ArrowRightLeft } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../services/api';
import ConfirmModal from '../../components/ui/ConfirmModal';
import MoveToOrgModal from '../../components/ui/MoveToOrgModal';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';

// ── Helper: FastAPI error → string ──────────────────────────────────────────
function extractError(e, t) {
    const detail = e?.response?.data?.detail;
    if (!detail) return e?.message || (t ? t('users.error_occurred', 'Xatolik yuz berdi') : 'Xatolik yuz berdi');
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => `${d.loc?.at(-1) ?? ''}: ${d.msg}`).join('; ');
    return JSON.stringify(detail);
}

// ── Reusable field/label styles ──────────────────────────────────────────────
const inputCls = "w-full px-4 py-2.5 rounded-xl bg-surface-2 border border-border text-primary text-sm focus:outline-none focus:border-brand transition-colors";
const labelCls = "block text-[11px] font-bold text-muted uppercase tracking-wider mb-1.5";

function Toggle({ label, checked, onChange }) {
    return (
        <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
                <div className="w-10 h-5 bg-border rounded-full peer peer-checked:bg-brand
          after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white
          after:rounded-full after:h-4 after:w-4 after:transition-all
          peer-checked:after:translate-x-5" />
            </label>
            <span className="text-sm font-medium text-secondary">{label}</span>
        </div>
    );
}

const EMPTY = { name_ru: '', name_uz: '', name_en: '', description_ru: '', description_uz: '', description_en: '', is_visible: true, icon_name: '', show_icon: true, icon_url: '', organization_id: '' };

const POPULAR_ICONS = [
    'LayoutDashboard', 'Activity', 'BarChart', 'PieChart', 'LineChart', 
    'Database', 'Layers', 'Box', 'Package', 'Briefcase', 'ShoppingCart',
    'Users', 'User', 'Settings', 'Shield', 'Lock', 'Key', 'Search',
    'Bell', 'MessageSquare', 'Mail', 'Phone', 'Globe', 'Map', 'Navigation',
    'Calendar', 'Clock', 'CheckCircle', 'AlertCircle', 'Info', 'HelpCircle',
    'FileText', 'File', 'Folder', 'Image', 'Video', 'Music', 'Mic',
    'Home', 'Star', 'Heart', 'Zap', 'Flame', 'Flashlight', 'Sun', 'Moon',
    'Cloud', 'Wind', 'Umbrella', 'Coffee', 'Truck', 'CreditCard'
];

function IconPicker({ value, onChange }) {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filtered = POPULAR_ICONS.filter(name => 
        name.toLowerCase().includes(search.toLowerCase())
    );

    const SelectedIcon = Icons[value] || Icons.LayoutDashboard;

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`${inputCls} flex items-center justify-between gap-2 text-left bg-surface hover:border-brand/50 transition-all shadow-sm ring-1 ring-border/50`}
            >
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center">
                        <SelectedIcon className="w-4 h-4 text-brand" />
                    </div>
                    <span className="font-medium">{value || 'Ikonkani tanlang...'}</span>
                </div>
                <Icons.ChevronDown className={`w-4 h-4 text-muted transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-[100] mt-2 w-full bg-surface border border-border rounded-2xl shadow-2xl p-3 animate-in fade-in zoom-in duration-200">
                    <div className="relative mb-3">
                        <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            autoFocus
                            className="w-full pl-10 pr-4 py-2 bg-surface-2 border border-border rounded-xl text-sm focus:outline-none focus:border-brand transition-colors"
                            placeholder="Qidirish (masalan: Chart, Star...)"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="max-h-64 overflow-y-auto grid grid-cols-4 gap-2 p-1 no-scrollbar">
                        {filtered.map(name => {
                            const Icon = Icons[name] || Icons.HelpCircle;
                            return (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => {
                                        onChange(name);
                                        setIsOpen(false);
                                        setSearch('');
                                    }}
                                    className={`
                                        flex flex-col items-center justify-center p-3 rounded-xl gap-2 transition-all duration-200
                                        ${value === name 
                                            ? 'bg-brand text-inverse shadow-lg shadow-brand/20 scale-105' 
                                            : 'hover:bg-brand/10 hover:text-brand text-muted grayscale hover:grayscale-0'}
                                    `}
                                    title={name}
                                >
                                    <Icon className="w-6 h-6" />
                                    <span className="text-[9px] font-bold truncate w-full text-center tracking-tighter uppercase">{name}</span>
                                </button>
                            );
                        })}
                        {filtered.length === 0 && (
                            <div className="col-span-full py-8 text-center text-muted text-xs">Ikonka topilmadi</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function CategoriesPage() {
    const { t } = useTranslation();
    const { currentOrg, currentOrgId, organizations } = useOrganization();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [iconFile, setIconFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formError, setFormError] = useState('');
    const { user: currentUser } = useAuth();
    const [togglingId, setTogglingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Tashkilotga ko'chirish — Super Admin yoki can_manage_organizations
    const canMove = organizations.length > 1 && !!(currentUser?.is_superadmin || currentUser?.can_manage_organizations);
    const [moveModal, setMoveModal] = useState({ open: false, items: [] });

    useEffect(() => { load(); }, [currentOrgId]);

    async function load() {
        setLoading(true);
        try {
            const res = await api.get('/admin/categories', {
                params: currentOrgId ? { organization_id: currentOrgId } : {}
            });
            setCategories(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }

    function openCreate() {
        setEditId(null);
        setForm({ ...EMPTY, organization_id: currentOrgId || '' });
        setIconFile(null);
        setFormError('');
        setShowForm(true);
    }

    function openEdit(cat) {
        setEditId(cat.id);
        setForm({
            name_ru: cat.name_ru,
            name_uz: cat.name_uz || '',
            name_en: cat.name_en || '',
            description_ru: cat.description_ru || '',
            description_uz: cat.description_uz || '',
            description_en: cat.description_en || '',
            is_visible: cat.is_visible,
            icon_name: cat.icon_name || '',
            show_icon: cat.show_icon,
            icon_url: cat.icon_url || '',
            organization_id: cat.organization_id || currentOrgId || ''
        });
        setIconFile(null);
        setFormError(''); setShowForm(true);
        setTimeout(() => { const el = document.getElementById('admin-content'); if (el) el.scrollTo({ top: 0, behavior: 'smooth' }); }, 50);
    }

    function f(key) {
        return { value: form[key], onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) };
    }

    async function handleSave(e) {
        if (e) e.preventDefault();
        if (saving) return;

        // ── Client-side required-field check ─────────────────────────────────
        const missing = [];
        if (!form.name_ru?.trim()) missing.push(t('categories.name_ru', 'Название (RU)'));
        if (!form.name_uz?.trim()) missing.push(t('categories.name_uz', 'Nomi (UZ)'));
        if (missing.length > 0) {
            setFormError(`${t('users.req_fields')}${missing.join(', ')}`);
            return;
        }
        // ─────────────────────────────────────────────────────────────────────

        setSaving(true); setFormError('');
        try {
            const fd = new FormData();
            
            // Send only changed or relevant fields to avoid bloat/errors
            const keysToSend = ['name_ru', 'name_uz', 'name_en', 'description_ru', 'description_uz', 'description_en', 'is_visible', 'icon_name', 'show_icon', 'organization_id'];
            keysToSend.forEach(k => {
                if (form[k] !== undefined && form[k] !== null && form[k] !== '') {
                    fd.append(k, String(form[k]));
                }
            });
            if (!editId && !form.organization_id && currentOrgId) {
                fd.append('organization_id', currentOrgId);
            }

            if (iconFile) {
                fd.append('icon', iconFile);
            }

            if (editId) {
                await api.put(`/admin/categories/${editId}`, fd);
            } else {
                await api.post('/admin/categories', fd);
            }
            
            // Close form only after successful save
            setShowForm(false);
            setForm(EMPTY);
            setIconFile(null);
            await load(); 
        } catch (e) { 
            console.error("Category save error:", e);
            setFormError(extractError(e)); 
        }
        finally { setSaving(false); }
    }

    async function handleDelete(id) {
        setPendingDeleteId(id);
        setIsModalOpen(true);
    }

    async function confirmDelete() {
        if (!pendingDeleteId) return;
        setIsDeleting(true);
        try {
            await api.delete(`/admin/categories/${pendingDeleteId}`);
            setIsModalOpen(false);
            setPendingDeleteId(null);
            load();
        } catch (e) {
            alert(extractError(e));
        } finally {
            setIsDeleting(false);
        }
    }

    async function toggleVisible(cat) {
        if (!currentUser?.is_superadmin && !currentUser?.can_edit_categories) return;
        setTogglingId(cat.id + '_vis');
        try {
            const fd = new FormData();
            fd.append('is_visible', String(!cat.is_visible));
            await api.put(`/admin/categories/${cat.id}`, fd);
            await load();
        }
        catch (e) { alert(extractError(e)); }
        finally { setTogglingId(null); }
    }

    async function toggleShowIcon(cat) {
        if (!currentUser?.is_superadmin && !currentUser?.can_edit_categories) return;
        setTogglingId(cat.id + '_icon');
        try {
            const fd = new FormData();
            fd.append('show_icon', String(!cat.show_icon));
            await api.put(`/admin/categories/${cat.id}`, fd);
            await load();
        }
        catch (e) { alert(extractError(e)); }
        finally { setTogglingId(null); }
    }

    async function handleDragEnd(result) {
        if (!result.destination) return;
        if (result.source.index === result.destination.index) return;
        if (!currentUser?.is_superadmin && !currentUser?.can_edit_category_order) return;

        const items = Array.from(categories);
        const [reordered] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reordered);

        const updatedItems = items.map((cat, idx) => ({ ...cat, order: idx }));
        setCategories(updatedItems);

        try {
            for (let i = 0; i < updatedItems.length; i++) {
                if (updatedItems[i].id !== categories[i].id || updatedItems[i].order !== categories[i].order) {
                    const fd = new FormData();
                    fd.append('order', String(i));
                    await api.put(`/admin/categories/${updatedItems[i].id}`, fd);
                }
            }
        } catch (e) {
            alert(extractError(e));
            load();
        }
    }

    const VITE_API_URL = import.meta.env.VITE_API_URL || '';

    const filteredCategories = useMemo(() => {
        if (!searchQuery.trim()) return categories;
        const q = searchQuery.toLowerCase();
        return categories.filter(cat =>
            (cat.name_ru || '').toLowerCase().includes(q) ||
            (cat.name_uz || '').toLowerCase().includes(q)
        );
    }, [categories, searchQuery]);

    return (
        <div className="space-y-6">
            {/* ── Header ── */}
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">{t('categories.title')}</h2>
                {(currentUser?.is_superadmin || currentUser?.can_create_categories) && (
                    <button
                        onClick={showForm ? () => setShowForm(false) : openCreate}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors
                ${showForm ? 'bg-surface-2 border border-border text-secondary' : 'bg-brand text-inverse hover:bg-brand-hover'}`}
                    >
                        {showForm ? <><X className="w-4 h-4" /> {t('categories.cancel')}</> : <><Plus className="w-4 h-4" /> {t('categories.add')}</>}
                    </button>
                )}
            </div>

            {/* ── Search ── */}
            {!showForm && (
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder={t('search')}
                        className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-surface border border-border text-primary text-sm focus:outline-none focus:border-brand transition-colors"
                    />
                </div>
            )}

            {/* ── Inline Form ── */}
            {showForm && (
                <div className="bg-surface border border-border rounded-2xl p-6 shadow-sm">
                    <h3 className="font-bold text-base mb-5 text-primary">
                        {editId ? t('categories.edit') : t('categories.new')}
                    </h3>
                    {formError && (
                        <div className="mb-5 p-4 rounded-xl bg-error/10 border border-error/20 text-error text-sm font-medium">
                            {formError}
                        </div>
                    )}
                    <form onSubmit={handleSave} noValidate>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className={labelCls}>{t('categories.name_ru', 'Название (RU)')} <span className="text-error">*</span></label>
                                <input required {...f('name_ru')} className={inputCls} placeholder={t('categories.placeholder_ru', 'Название на русском')} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('categories.name_uz', 'Nomi (UZ)')}</label>
                                <input {...f('name_uz')} className={inputCls} placeholder={t('categories.placeholder_uz', "O'zbekcha nomi")} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('categories.name_en', 'Name (EN)')}</label>
                                <input {...f('name_en')} className={inputCls} placeholder={t('categories.placeholder_en', 'Name in English')} />
                            </div>
                            <div>
                                <label className={labelCls}>{t('categories.icon_lucide')}</label>
                                <IconPicker 
                                    value={form.icon_name} 
                                    onChange={val => setForm(p => ({ ...p, icon_name: val }))} 
                                />
                                <p className="mt-1 text-[10px] text-muted-2 italic">Lucide ikonkalari nomini tanlang</p>
                            </div>
                            <div>
                                <label className={labelCls}>{t('categories.icon_file')}</label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        accept=".svg, .png, image/svg+xml, image/png"
                                        onChange={e => setIconFile(e.target.files[0])}
                                        className="hidden"
                                        id="icon-upload"
                                    />
                                    <div className="flex items-center gap-3">
                                        <label
                                            htmlFor="icon-upload"
                                            className={`flex items-center gap-2 flex-1 px-4 py-2.5 rounded-xl border border-dashed border-border cursor-pointer hover:border-brand transition-colors text-sm ${iconFile ? 'text-brand font-medium' : 'text-muted'}`}
                                        >
                                            <Upload className="w-4 h-4" />
                                            {iconFile ? iconFile.name : t('categories.icon_file')}
                                        </label>
                                        {(iconFile || form.icon_url || form.icon_name) && (
                                            <div className="w-10 h-10 rounded-lg bg-surface-2 border border-border flex items-center justify-center text-brand overflow-hidden relative p-1">
                                                {iconFile ? (
                                                    <img src={URL.createObjectURL(iconFile)} alt="Preview" className="w-full h-full object-contain" />
                                                ) : form.icon_url ? (
                                                    <img src={`${VITE_API_URL}${form.icon_url}`} alt="Current" className="w-full h-full object-contain" />
                                                ) : form.icon_name ? (
                                                    (() => {
                                                        const Icon = Icons[form.icon_name] || LayoutDashboard;
                                                        return <Icon className="w-5 h-5" />;
                                                    })()
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>{t('categories.visibility')}</label>
                                <div className="mt-2">
                                    <Toggle
                                        label={t('categories.active')}
                                        checked={form.is_visible}
                                        onChange={e => setForm(p => ({ ...p, is_visible: e.target.checked }))}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>{t('categories.show_icon')}</label>
                                <div className="mt-2">
                                    <Toggle
                                        label={t('categories.show_icon')}
                                        checked={form.show_icon}
                                        onChange={e => setForm(p => ({ ...p, show_icon: e.target.checked }))}
                                    />
                                </div>
                            </div>
                            <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={labelCls}>{t('categories.desc_ru', 'Описание (RU)')}</label>
                                    <textarea rows={2} {...f('description_ru')} className={`${inputCls} resize-none`} placeholder={t('categories.placeholder_desc_ru', 'Описание на русском...')} />
                                </div>
                                <div>
                                    <label className={labelCls}>{t('categories.desc_uz', 'Tavsif (UZ)')}</label>
                                    <textarea rows={2} {...f('description_uz')} className={`${inputCls} resize-none`} placeholder={t('categories.placeholder_desc_uz', "O'zbekcha tavsif...")} />
                                </div>
                                <div>
                                    <label className={labelCls}>{t('categories.desc_en', 'Description (EN)')}</label>
                                    <textarea rows={2} {...f('description_en')} className={`${inputCls} resize-none`} placeholder={t('categories.placeholder_desc_en', 'Description in English...')} />
                                </div>
                            </div>
                        </div>

                        {formError && (
                            <p className="mt-3 text-sm text-error font-medium bg-error/5 border border-error/20 rounded-lg px-4 py-2">
                                {formError}
                            </p>
                        )}

                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="px-6 py-2.5 rounded-xl border border-border text-secondary hover:bg-surface-2 text-sm font-semibold transition-colors"
                            >
                                {t('categories.cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="w-40 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-brand text-inverse text-sm font-bold hover:bg-brand-hover disabled:opacity-60 transition-colors"
                            >
                                {saving ? (
                                    <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                ) : t('categories.save')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ── Table ── */}
            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <table className="w-full text-left text-sm">
                            <thead className="bg-surface-2 border-b border-border text-muted text-xs uppercase">
                                <tr>
                                    <th className="px-6 py-4 font-semibold w-12">#</th>
                                    <th className="px-6 py-4 font-semibold">{t('categories.icon_lucide')}</th>
                                    <th className="px-6 py-4 font-semibold">{t('categories.name')}</th>
                                    <th className="px-6 py-4 font-semibold">{t('categories.creator')}</th>
                                    <th className="px-6 py-4 font-semibold">{t('categories.visibility')}</th>
                                    <th className="px-6 py-4 font-semibold">{t('categories.show_icon')}</th>
                                    <th className="px-6 py-4 font-semibold text-right">{t('categories.actions')}</th>
                                </tr>
                            </thead>
                            <Droppable droppableId="categories">
                                {(provided) => (
                                    <tbody className="divide-y divide-border" {...provided.droppableProps} ref={provided.innerRef}>

                                        {loading ? (
                                            <tr><td colSpan={7} className="px-6 py-10 text-center text-muted">{t('categories.loading')}</td></tr>
                                        ) : filteredCategories.length === 0 ? (
                                            <tr><td colSpan={7} className="px-6 py-10 text-center text-muted">{t('categories.not_found')}</td></tr>
                                        ) : filteredCategories.map((cat, index) => (
                                            <Draggable
                                                key={cat.id}
                                                draggableId={cat.id}
                                                index={index}
                                                isDragDisabled={!currentUser?.is_superadmin && !currentUser?.can_edit_category_order}
                                            >
                                                {(provided, snapshot) => (
                                                    <tr
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`hover:bg-surface-2/50 transition-colors ${snapshot.isDragging ? 'bg-surface shadow-lg z-50 rounded-xl outline outline-1 outline-brand' : ''}`}
                                                        style={{ ...provided.draggableProps.style }}
                                                    >
                                                        <td className="px-6 py-4">
                                                            <div {...provided.dragHandleProps} className={`w-6 h-6 flex items-center justify-center rounded ${(!currentUser?.is_superadmin && !currentUser?.can_edit_category_order) ? 'text-muted/30 cursor-not-allowed' : 'text-muted hover:text-primary cursor-grab active:cursor-grabbing'}`}>
                                                                <GripVertical className="w-4 h-4" />
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                        <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-surface-2 border border-border flex items-center justify-center text-brand overflow-hidden relative"
                                                            style={{ boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)' }}
                                                        >
                                                            {cat.show_icon && cat.icon_url ? (
                                                                <div className="w-full h-full flex items-center justify-center p-1.5">
                                                                    <img 
                                                                        src={cat.icon_url.startsWith('http') ? cat.icon_url : `${VITE_API_URL}${cat.icon_url}`} 
                                                                        alt="" 
                                                                        className="w-full h-full object-contain"
                                                                        style={{ filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.4))' }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                (() => {
                                                                    const Icon = Icons[cat.icon_name] || Icons.LayoutDashboard;
                                                                    return <Icon className="w-5 h-5" />;
                                                                })()
                                                            )}
                                                        </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="font-medium text-primary">{i18n.language === 'en' ? (cat.name_en || cat.name_ru || cat.name_uz) : (i18n.language === 'uz' ? (cat.name_uz || cat.name_ru) : cat.name_ru)}</div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="px-2 py-1 rounded-md bg-surface-2 border border-border text-xs text-secondary font-medium">
                                                                @{cat.created_by_login || 'tizim'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            {(!currentUser?.is_superadmin && !currentUser?.can_edit_categories) ? (
                                                                <div className={`flex items-center gap-1.5 w-fit text-[11px] font-bold px-2.5 py-1 rounded-full mx-auto
                                                                    ${cat.is_visible ? 'bg-success/10 text-success' : 'bg-surface-2 text-muted'}`}>
                                                                    {cat.is_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                                                    {cat.is_visible ? t('categories.active') : t('categories.cancel')}
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => toggleVisible(cat)}
                                                                    disabled={togglingId === cat.id + '_vis'}
                                                                    className={`flex items-center gap-1.5 w-fit text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors mx-auto
                                                                    ${cat.is_visible ? 'bg-success/10 text-success hover:bg-success/20' : 'bg-surface-2 text-muted hover:bg-surface-3'}
                                                                    disabled:opacity-50 disabled:cursor-not-allowed`}>
                                                                    {togglingId === cat.id + '_vis' ? (
                                                                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                                    ) : (
                                                                        cat.is_visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />
                                                                    )}
                                                                    {cat.is_visible ? t('categories.active') : t('categories.cancel')}
                                                                </button>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            {(!currentUser?.is_superadmin && !currentUser?.can_edit_categories) ? (
                                                                <div className={`flex items-center gap-1.5 w-fit text-[11px] font-bold px-2.5 py-1 rounded-full mx-auto
                                                                    ${cat.show_icon ? 'bg-brand/10 text-brand' : 'bg-surface-2 text-muted'}`}>
                                                                    {cat.show_icon ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    onClick={() => toggleShowIcon(cat)}
                                                                    disabled={togglingId === cat.id + '_icon'}
                                                                    className={`flex items-center gap-1.5 w-fit text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors mx-auto
                                                                    ${cat.show_icon ? 'bg-brand/10 text-brand hover:bg-brand/20' : 'bg-surface-2 text-muted hover:bg-surface-3'}
                                                                    disabled:opacity-50 disabled:cursor-not-allowed`}>
                                                                    {togglingId === cat.id + '_icon' ? (
                                                                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                                    ) : (
                                                                        cat.show_icon ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />
                                                                    )}
                                                                </button>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-right space-x-2">
                                                            {canMove && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setMoveModal({ open: true, items: [{ id: cat.id, title: i18n.language === 'en' ? (cat.name_en || cat.name_ru || cat.name_uz) : (i18n.language === 'uz' ? (cat.name_uz || cat.name_ru) : cat.name_ru) }] })}
                                                                    className="p-2 text-muted hover:text-brand bg-surface-2 rounded-lg border border-border transition-colors"
                                                                    title={t('organizations.move_title', "Tashkilotga ko'chirish")}
                                                                >
                                                                    <ArrowRightLeft className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => openEdit(cat)}
                                                                disabled={!currentUser?.is_superadmin && !currentUser?.can_edit_categories}
                                                                className="p-2 text-muted hover:text-brand bg-surface-2 rounded-lg border border-border transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                                                                <Edit className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={!currentUser?.is_superadmin && !currentUser?.can_delete_categories}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleDelete(cat.id);
                                                                }}
                                                                className="p-2 text-muted hover:text-error bg-surface-2 rounded-lg border border-border transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                                title={t('categories.actions')}
                                                            >
                                                                <Trash2 className="w-4 h-4 pointer-events-none" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </table>
                    </DragDropContext>
                </div>
            </div>

            <MoveToOrgModal
                open={moveModal.open}
                onClose={() => setMoveModal({ open: false, items: [] })}
                itemType="categories"
                items={moveModal.items}
                onMoved={() => load()}
            />

            <ConfirmModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={confirmDelete}
                isLoading={isDeleting}
                title={t('categories.delete_confirm_title')}
                message={t('categories.delete_confirm_msg')}
            />
        </div>
    );
}
